import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Avatar,
  Chip,
  Icon,
  TextField,
  Grid,
  Divider,
  Alert,
  Dialog,
  DialogContent,
  DialogActions,
  ListItem,
  ListItemText,
  DialogTitle,
} from "@mui/material";
import Africastalking from "africastalking-client";
import MDButton from "theme/components/MDButton";
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import axios from "axios.js";
import { useAwaitDialog } from "app/components/status_dialog";

export default function CallCenterDialer({
  agentRef,
  contact,
  onIncomingCall,
  onCallCompleted,
  onTransferCall,
  onCallActive,
  onFetchHistory,
  onFetchCallerId,
  onSaveContact,
  isTransferred,
}) {
  const [state, setState] = useState("notready");
  const [isReady, setIsReady] = useState(false);
  const [callWasConnected, setCallWasConnected] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [callerName, setCallerName] = useState(""); // Tracks active caller ID label
  const [timer, setTimer] = useState(0);
  const [wakeLock, setWakeLock] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [micPermissionDenied, setMicPermissionDenied] = useState(false); // <-- Tracks Mic Permission state

  const ringtoneRef = useRef(null);
  const clientRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(0);
  const directionRef = useRef(null); // "inbound" | "outbound"
  const activeNumberRef = useRef("");
  const STORAGE_KEY = "ldr_af_webrtc_token";
  const contactRef = useRef(null);
  const callWasConnectedRef = useRef(false);

  const [showContactDialog, setShowContactDialog] = useState(false);
  const statusDialog = useAwaitDialog();

  let reconnectTimeout;

  async function getFreshToken() {
    let token = null;
    let expires = null;

    await axios
      .get(`agents/call_centers/${agentRef}/dialer.json`)
      .then(async (result) => {
        if (result.data.status === "success") {
          token = result.data.data.token;
          expires = result.data.data.expires;
        } else {
          await statusDialog.show(result.data.data.message, "error", "Failed");
        }
      })
      .catch(() => {});

    return { token, expires };
  }

  window.addEventListener("unhandledrejection", (event) => {
    console.error("UNHANDLED PROMISE:", event.reason);
  });

  window.addEventListener("error", (event) => {
    console.error("GLOBAL ERROR:", event.error);
  });

  async function requestMicPermission() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      streamRef.current = stream;
      setMicPermissionDenied(false);
      return true;
    } catch (err) {
      if (
        err.name === "NotAllowedError" ||
        err.name === "PermissionDeniedError"
      ) {
        setMicPermissionDenied(true);
      } else {
        console.error("Microphone access error:", err);
      }
      return false;
    }
  }

  async function init() {
    try {
      const hasMic = await requestMicPermission();
      if (!hasMic) {
        setState("notready");
        return; // Pause initialization until mic permission is granted
      }

      const res = await getFreshToken();
      if (!res?.token || !res?.expires) {
        throw new Error("Failed to fetch valid token");
      }

      setupClient(res.token);
    } catch (err) {
      console.error("Token init error:", err);

      // retry after delay if mic permission wasn't explicitly denied
      if (!micPermissionDenied) {
        setTimeout(init, 3000);
      }
    }
  }

  function setupClient(validToken) {
    if (clientRef.current) {
      try {
        clientRef.current.destroy?.();
      } catch {}
    }

    const client = new Africastalking.Client(validToken);
    clientRef.current = client;

    clientRef.current.on("ready", () => {
      setState("ready");
      setIsReady(true);
    });

    clientRef.current.on("notready", () => {
      setState("notready");
      setIsReady(false);
    });

    clientRef.current.on("calling", () => {
      setState("calling");
    });

    clientRef.current.on("incomingcall", async (params) => {
      const callerNum = params?.from;
      directionRef.current = "inbound";
      activeNumberRef.current = callerNum;

      setPhoneNumber(callerNum);
      setCallerName(callerNum); // Fallback to raw phone number initially
      contactRef.current = { phone_number: callerNum, name: callerNum };

      setState("incomingcall");
      callWasConnectedRef.current = false;
      ringtoneRef.current?.play().catch(() => {});

      // Notify parent about incoming call
      onIncomingCall?.(callerNum);

      // Fetch Caller ID from parent if handler is provided
      if (onFetchCallerId && callerNum) {
        try {
          const resolvedContact = await onFetchCallerId(callerNum);
          if (resolvedContact) {
            setCallerName(resolvedContact.name);
            contactRef.current = {
              phone_number: callerNum,
              name: resolvedContact.name,
              attributes: resolvedContact.attributes,
            };
          }
        } catch (error) {
          console.error("Error fetching caller ID:", error);
        }
      }
    });

    clientRef.current.on("callaccepted", () => {
      callWasConnectedRef.current = true;

      setState("callaccepted");
      setIsMuted(false);
      clientRef.current?.unmuteAudio();
      ringtoneRef.current?.pause();
      if (ringtoneRef.current) ringtoneRef.current.currentTime = 0;

      // Bubble up to parent ONLY when the connection is officially bridged
      if (onCallActive) {
        onCallActive({
          callerNumber: activeNumberRef.current,
          callerName: callerName || activeNumberRef.current,
          direction: directionRef.current,
        });
      }
    });

    clientRef.current.on("hangup", () => {
      if (callWasConnectedRef.current) {
        onCallCompleted?.(contactRef.current);
      }

      setState("hangup");
      setIsMuted(false);
      clientRef.current?.unmuteAudio();
      ringtoneRef.current?.pause();
      if (ringtoneRef.current) ringtoneRef.current.currentTime = 0;

      setTimeout(() => setState("ready"), 1500);
    });

    clientRef.current.on("offline", reconnect);
    clientRef.current.on("closed", reconnect);
  }

  function reconnect() {
    setState("offline");
    setIsReady(false);

    clearTimeout(reconnectTimeout);
    reconnectTimeout = setTimeout(() => {
      init();
    }, 2000);
  }

  useEffect(() => {
    init();
    return () => {
      clearTimeout(reconnectTimeout);
      clientRef.current?.destroy?.();
    };
  }, []);

  useEffect(() => {
    if (!contact?.phone_number) {
      setPhoneNumber(null);
      setCallerName("");
      contactRef.current = null;
      return;
    }

    setPhoneNumber(contact.phone_number);
    setCallerName(contact.name || contact.phone_number);
    contactRef.current = contact;
    handleCall(contact.phone_number);
  }, [contact]);

  useEffect(() => {
    ringtoneRef.current = new Audio("/audio/002.mp3");
    ringtoneRef.current.loop = true;
  }, []);

  useEffect(() => {
    timerRef.current = timer;
  }, [timer]);

  // =========================
  // WAKE LOCK
  // =========================
  const requestWakeLock = async () => {
    try {
      if ("wakeLock" in navigator) {
        const lock = await navigator.wakeLock.request("screen");
        setWakeLock(lock);
      }
    } catch {}
  };

  const releaseWakeLock = async () => {
    if (wakeLock) {
      await wakeLock.release();
      setWakeLock(null);
    }
  };

  useEffect(() => {
    if (state === "calling" || state === "callaccepted") {
      requestWakeLock();
    } else {
      releaseWakeLock();
    }
  }, [state]);

  useEffect(() => {
    const handleVisibility = () => {
      if (
        document.visibilityState === "visible" &&
        (state === "calling" || state === "callaccepted")
      ) {
        requestWakeLock();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [state]);

  // Refresh Page every n minutes if not in any active call
  // useEffect(() => {
  //   const REFRESH_MINUTES_MS = 5 * 60 * 1000;

  //   const intervalId = setInterval(() => {
  //     if (!["calling", "callaccepted", "incomingcall"].includes(state)) {
  //       window.location.reload();
  //     }
  //   }, REFRESH_MINUTES_MS);

  //   return () => clearInterval(intervalId);
  // }, [state]);

  // =========================
  // TIMER
  // =========================
  useEffect(() => {
    let interval;
    if (state === "callaccepted") {
      interval = setInterval(() => setTimer((t) => t + 1), 1000);
    } else {
      setTimer(0);
    }
    return () => clearInterval(interval);
  }, [state]);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    if (isTransferred && state === "callaccepted") {
      clientRef.current.dtmf("*");
      setTimeout(() => {
        handleHangup();
      }, 300);
    }
  }, [isTransferred]);

  const handleCall = async (number = null) => {
    if (!isReady) return;

    const dialNumber = number || phoneNumber;
    if (!dialNumber || !clientRef.current) return;

    directionRef.current = "outbound";
    activeNumberRef.current = dialNumber;

    callWasConnectedRef.current = false;

    setTimeout(() => {
      clientRef.current.call(dialNumber);
    }, 300);
  };

  const handleHangup = () => {
    if (!isReady) return;
    clientRef.current.hangup();
  };

  const handleAccept = () => {
    if (!isReady) return;
    clientRef.current.answer();
  };

  const handleReject = () => {
    if (!isReady) return;
    clientRef.current.hangup();
  };

  const handleTransfer = () => {
    if (onTransferCall) {
      onTransferCall({
        direction: directionRef.current,
        contact: contactRef.current,
        duration: timer,
      });
    }
  };

  const handleContactHistory = () => {
    const activeNum = activeNumberRef.current || phoneNumber;
    if (onFetchHistory && activeNum) {
      onFetchHistory(activeNum);
    }
  };

  const toggleMute = () => {
    if (!clientRef.current) return;
    if (isMuted) {
      clientRef.current.unmuteAudio();
      setIsMuted(false);
    } else {
      clientRef.current.muteAudio();
      setIsMuted(true);
    }
  };

  const config = {
    ready: { label: "Ready", color: "success", icon: "call" },
    notready: { label: "Not Ready", color: "default", icon: "phone_disabled" },
    calling: { label: "Calling...", color: "success", icon: "call" },
    incomingcall: {
      label: "Incoming Call",
      color: "info",
      icon: "call_received",
    },
    callaccepted: { label: "In Call", color: "success", icon: "call" },
    hangup: { label: "Call Ended", color: "error", icon: "call_end" },
    offline: { label: "Offline", color: "error", icon: "wifi_off" },
    closed: { label: "Disconnected", color: "default", icon: "wifi_off" },
  }[state];

  // Helper check to determine if the caller isn't saved as an existing contact name
  const isUnknownNumber =
    phoneNumber &&
    (!callerName || callerName === phoneNumber) &&
    (!contactRef.current?.name || contactRef.current?.name === phoneNumber);

  return (
    <Box sx={{ width: "100%", maxWidth: 420, mx: "auto" }}>
      <Dialog open={showContactDialog} fullWidth maxWidth="xs">
        <DialogTitle>Contact Info</DialogTitle>
        <DialogContent>
          <MDBox>
            <Stack display="flex" gap={2} mb={3} justifyContent="vertical">
              <MDBox>
                <ListItemText
                  primary={
                    <MDTypography
                      variant="h6"
                      fontWeight="regular"
                      fontSize={13}
                    >
                      Name
                    </MDTypography>
                  }
                  secondary={contactRef.current?.name}
                />
              </MDBox>
              <MDBox>
                <ListItemText
                  primary={
                    <MDTypography
                      variant="h6"
                      fontWeight="regular"
                      fontSize={13}
                    >
                      Phone Number
                    </MDTypography>
                  }
                  secondary={contactRef.current?.phone_number}
                />
              </MDBox>

              {contactRef.current?.tag && (
                <MDBox>
                  <ListItemText
                    primary={
                      <MDTypography
                        variant="h6"
                        fontWeight="regular"
                        fontSize={13}
                      >
                        Tag
                      </MDTypography>
                    }
                    secondary={contactRef.current?.tag}
                  />
                </MDBox>
              )}
            </Stack>
            {contactRef.current?.attributes &&
              typeof contactRef.current.attributes === "object" &&
              Object.keys(contactRef.current.attributes).length > 0 && (
                <MDBox mt={2}>
                  <MDTypography
                    variant="button"
                    fontWeight="medium"
                    color="text"
                  >
                    Attributes
                  </MDTypography>
                  <Divider my={3} />
                  <Stack display="flex" gap={2} mt={1}>
                    {Object.entries(contactRef.current.attributes).map(
                      ([key, value]) => (
                        <MDBox key={key}>
                          <ListItemText
                            primary={
                              <MDTypography
                                variant="h6"
                                fontWeight="regular"
                                fontSize={13}
                              >
                                {key
                                  .replace(/_/g, " ")
                                  .replace(/\b\w/g, (letter) =>
                                    letter.toUpperCase(),
                                  )}
                              </MDTypography>
                            }
                            secondary={
                              value === null ||
                              value === undefined ||
                              value === ""
                                ? "---"
                                : typeof value === "object"
                                ? JSON.stringify(value)
                                : String(value)
                            }
                          />
                        </MDBox>
                      ),
                    )}
                  </Stack>
                </MDBox>
              )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="outlined"
            color="dark"
            iconOnly
            onClick={() => setShowContactDialog(false)}
          >
            <Icon>clear</Icon>
          </MDButton>
        </DialogActions>
      </Dialog>
      <Box sx={{ p: { xs: 2, sm: 3 }, textAlign: "center" }}>
        {/* MICROPHONE PERMISSION BANNER */}
        {micPermissionDenied && (
          <MDBox mb={2}>
            <Alert severity="warning">
              <MDTypography variant="body2">
                Microphone access is required to place or receive calls. Please
                grant permission in your browser settings.
              </MDTypography>
              <MDBox mt={1} align="center">
                <MDButton
                  fullWidth
                  color="dark"
                  size="small"
                  onClick={() => window.location.reload()}
                  startIcon={<Icon>mic</Icon>}
                >
                  Grant
                </MDButton>
              </MDBox>
            </Alert>
          </MDBox>
        )}

        <MDBox mt={-1}>
          {["offline", "closed", "notready"].includes(state) ? (
            <Chip
              label={`Status: ${config.label}`}
              color={config.color}
              onDelete={() => {
                window.location.reload();
              }}
              deleteIcon={<Icon>refresh</Icon>}
            />
          ) : (
            <Chip label={`Status: ${config.label}`} color={config.color} />
          )}
        </MDBox>

        {/* DISPLAY CALLER NAME OR NUMBER */}
        {(contactRef.current || callerName) && (
          <MDBox mt={3}>
            <MDTypography variant="h6" fontSize={16}>
              {callerName || contactRef.current?.name || phoneNumber}
            </MDTypography>
            {callerName && phoneNumber && callerName !== phoneNumber && (
              <MDTypography variant="caption" color="text">
                {phoneNumber}
              </MDTypography>
            )}
            {contactRef.current?.name && (
              <MDBox mt={0.4}>
                <MDButton
                  variant="outlined"
                  color="dark"
                  size="small"
                  startIcon={<Icon>info</Icon>}
                  onClick={() => {
                    if (contactRef.current?.name) {
                      setShowContactDialog(true);
                    }
                  }}
                >
                  Contact Info
                </MDButton>
              </MDBox>
            )}
          </MDBox>
        )}

        <MDBox mb={3}>
          <Divider />
        </MDBox>
        <Stack spacing={2} alignItems="center">
          {state === "callaccepted" && (
            <Typography
              variant="h5"
              color="success.main"
              fontWeight="bold"
              sx={{ letterSpacing: 1 }}
            >
              {formatTime(timer)}
            </Typography>
          )}

          {/* OUTGOING */}
          {state === "calling" && (
            <Stack spacing={1} alignItems="center">
              <Avatar sx={{ width: 48, height: 48, bgcolor: "primary.main" }}>
                <Icon>person</Icon>
              </Avatar>

              <Icon sx={{ animation: "moveUp 1s infinite" }}>arrow_upward</Icon>

              <Avatar sx={{ width: 56, height: 56, bgcolor: "success.main" }}>
                <Icon>call</Icon>
              </Avatar>
            </Stack>
          )}

          {/* INCOMING */}
          {state === "incomingcall" && (
            <Stack spacing={1} alignItems="center">
              <Avatar sx={{ width: 56, height: 56, bgcolor: "success.main" }}>
                <Icon>call</Icon>
              </Avatar>

              <Icon sx={{ animation: "moveDown 1s infinite" }}>
                arrow_downward
              </Icon>

              <Avatar sx={{ width: 48, height: 48, bgcolor: "primary.main" }}>
                <Icon>support_agent</Icon>
              </Avatar>

              <Typography mt={1} variant="body2">
                {config.label}
              </Typography>
            </Stack>
          )}

          {/* CONNECTED */}
          {state === "callaccepted" && (
            <Box sx={{ position: "relative", width: 80, height: 80 }}>
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  bgcolor: isMuted ? "warning.main" : "success.main",
                  transform: "translate(-50%, -50%)",
                  animation: "ripple 2s infinite",
                  opacity: 0.2,
                }}
              />
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: isMuted ? "warning.main" : "success.main",
                  color: "#fff",
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  transition: "background-color 0.3s ease",
                }}
              >
                <Icon>{isMuted ? "mic_off" : "call"}</Icon>
              </Avatar>
            </Box>
          )}

          {/* DEFAULT */}
          {state !== "calling" &&
            state !== "incomingcall" &&
            state !== "callaccepted" && (
              <Avatar sx={{ width: 64, height: 64 }}>
                <Icon>{config.icon}</Icon>
              </Avatar>
            )}

          <Stack spacing={1} width="100%">
            <MDBox mt={3}>
              {state === "ready" && phoneNumber && (
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent="center"
                  mt={2}
                >
                  <MDButton
                    fullWidth
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      handleCall();
                    }}
                    disabled={!isReady}
                    startIcon={<Icon>call</Icon>}
                  >
                    Call Again
                  </MDButton>

                  {/* Show Save Contact if caller number isn't saved yet */}
                  {callWasConnectedRef.current && isUnknownNumber && (
                    <MDButton
                      fullWidth
                      variant="outlined"
                      color="info"
                      startIcon={<Icon>person_add</Icon>}
                      onClick={() => onSaveContact?.(phoneNumber)}
                    >
                      Save Contact
                    </MDButton>
                  )}
                </Stack>
              )}

              {state === "calling" && (
                <MDButton
                  variant="contained"
                  fullWidth
                  color="error"
                  onClick={handleHangup}
                >
                  Cancel
                </MDButton>
              )}

              {state === "incomingcall" && (
                <Grid container spacing={3}>
                  <Grid item xs={6} md={6} lg={6}>
                    <MDButton
                      variant="contained"
                      fullWidth
                      color="primary"
                      onClick={handleAccept}
                      startIcon={<Icon>check_circle</Icon>}
                    >
                      Accept
                    </MDButton>
                  </Grid>
                  <Grid item xs={6} md={6} lg={6}>
                    <MDButton
                      variant="outlined"
                      fullWidth
                      color="error"
                      onClick={handleReject}
                      startIcon={<Icon>close</Icon>}
                    >
                      Reject
                    </MDButton>
                  </Grid>
                </Grid>
              )}

              {state === "callaccepted" && (
                <Grid
                  container
                  spacing={{ xs: 2, sm: 1 }}
                  justifyContent="center"
                  alignItems="center"
                >
                  {/* History Button Trigger */}
                  <Grid item xs={6} sm={3} align="center">
                    <Stack spacing={1} alignItems="center">
                      <MDButton
                        variant="outlined"
                        color="secondary"
                        iconOnly
                        circular
                        onClick={handleContactHistory}
                        sx={{
                          width: 52,
                          height: 52,
                        }}
                      >
                        <Icon>history</Icon>
                      </MDButton>
                      <MDTypography variant="caption" color="text">
                        History
                      </MDTypography>
                    </Stack>
                  </Grid>

                  {/* Transfer Button */}
                  <Grid item xs={6} sm={3} align="center">
                    <Stack spacing={1} alignItems="center">
                      <MDButton
                        variant="outlined"
                        color="info"
                        iconOnly
                        circular
                        onClick={handleTransfer}
                        sx={{
                          width: 52,
                          height: 52,
                        }}
                      >
                        <Icon>swap_horiz</Icon>
                      </MDButton>
                      <MDTypography variant="caption" color="text">
                        Transfer
                      </MDTypography>
                    </Stack>
                  </Grid>

                  {/* Mute Button */}
                  <Grid item xs={6} sm={3} align="center">
                    <Stack spacing={1} alignItems="center">
                      <MDButton
                        variant={isMuted ? "contained" : "outlined"}
                        color={isMuted ? "warning" : "info"}
                        iconOnly
                        circular
                        onClick={toggleMute}
                        sx={{
                          width: 52,
                          height: 52,
                          transition: "all 0.2s ease",
                        }}
                      >
                        <Icon>{isMuted ? "mic_off" : "mic"}</Icon>
                      </MDButton>
                      <MDTypography
                        variant="caption"
                        color={isMuted ? "warning" : "text"}
                      >
                        {isMuted ? "Muted" : "Mute"}
                      </MDTypography>
                    </Stack>
                  </Grid>

                  {/* End Call Button */}
                  <Grid item xs={6} sm={3} align="center">
                    <Stack spacing={1} alignItems="center">
                      <MDButton
                        iconOnly
                        circular
                        variant="contained"
                        color="error"
                        onClick={handleHangup}
                        sx={{
                          width: 52,
                          height: 52,
                        }}
                      >
                        <Icon>call_end</Icon>
                      </MDButton>
                      <MDTypography variant="caption" color="text">
                        End Call
                      </MDTypography>
                    </Stack>
                  </Grid>
                </Grid>
              )}
            </MDBox>
          </Stack>
        </Stack>
      </Box>

      <style>
        {`
          @keyframes moveUp {
            0% { transform: translateY(10px); opacity: 0; }
            50% { opacity: 1; }
            100% { transform: translateY(-10px); opacity: 0; }
          }

          @keyframes moveDown {
            0% { transform: translateY(-10px); opacity: 0; }
            50% { opacity: 1; }
            100% { transform: translateY(10px); opacity: 0; }
          }

          @keyframes ripple {
            0% { transform: translate(-50%, -50%) scale(0.8); opacity: 0.4; }
            70% { transform: translate(-50%, -50%) scale(1.6); opacity: 0; }
            100% { opacity: 0; }
          }
        `}
      </style>
    </Box>
  );
}
